import { useState, useRef } from "react";
import { TENANT_DOMAIN } from "../../utils/domain";
import { ERROR_MESSAGES, ITEMS_PER_PAGE } from "../../constant/constant";
import { useAxios } from "../../utils/useAxios";

/**
 * @typedef {object} SubSchoolItem
 * @property {number} id - Sub-school ID
 * @property {string} name - Sub-school name
 * @property {number} org_id - Organization ID
 * @property {string|null} image_url - Sub-school image URL
 * @property {number} license_assigned - Assigned licenses count
 */

/**
 * @typedef {object} SubSchoolsMeta
 * @property {number} current_page - Current page number
 * @property {number} per_page - Items per page
 * @property {number} total - Total number of items
 * @property {number} last_page - Last page number
 */

/**
 * @typedef {object} SubSchoolsData
 * @property {SubSchoolItem[]} data - Array of sub-school items
 * @property {SubSchoolsMeta} meta - Pagination metadata
 */

/**
 * @typedef {object} OrgStats
 * @property {number} total_licenses - Total licenses purchased
 * @property {number} assigned_licenses - Total licenses assigned to schools
 * @property {number} available_licenses - Available licenses remaining
 */

/**
 * @typedef {object} ApiResponseData
 * @property {SubSchoolsData} sub_schools - Sub-schools data with pagination
 * @property {{ org: OrgStats }} stats - Organization statistics
 */

/**
 * @typedef {object} GetSubSchoolDropDownListParams
 * @property {number | string} orgId - Organization ID (required)
 * @property {number} [page] - Page number for pagination
 * @property {number} [per_page] - Number of items per page
 * @property {string} [search] - Search query to filter schools by name
 */

/**
 * Custom hook to fetch sub-school dropdown list for an organization
 * Supports pagination, search, and appending results for infinite scroll
 */
export const useGetSubSchoolDropDownList = () => {
  const [subSchools, setSubSchools] = useState(
    /** @type {SubSchoolItem[] | null | undefined} */(undefined),
  );
  const [orgStats, setOrgStats] = useState(
    /** @type {OrgStats | null | undefined} */(undefined),
  );
  const [meta, setMeta] = useState(
    /** @type {SubSchoolsMeta | null | undefined} */(undefined),
  );
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  // Track the latest request to prevent race conditions
  const latestRequestId = useRef(0);

  /**
   * Fetch Sub-School Dropdown List
   * @async
   * @param {GetSubSchoolDropDownListParams} params
   * @returns {Promise<void>}
   */
  const getSubSchoolDropDownList = async ({
    orgId,
    page = 1,
    per_page = ITEMS_PER_PAGE,
    search,
  } = {}) => {
    if (!orgId) {
      setError("Organization ID is required");
      setLoading(false);
      return;
    }

    const requestId = ++latestRequestId.current;
    const isLoadingMore = page > 1;

    try {
      if (isLoadingMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError("");

      const query = new URLSearchParams();
      query.append("page", page);
      query.append("per_page", per_page);

      if (search) {
        query.append("filter[name]", search);
      }

      const url = `${TENANT_DOMAIN}/references/organization/${orgId}/sub-schools?${query.toString()}`;
      const response = await axiosInstance.get(url);

      // Ignore stale responses
      if (requestId !== latestRequestId.current) return;

      /** @type {ApiResponseData} */
      const data = response.data?.data;

      const newSchools = data?.sub_schools?.data || [];

      if (isLoadingMore) {
        // Append for infinite scroll
        setSubSchools((prev) => [...(prev || []), ...newSchools]);
      } else {
        setSubSchools(newSchools);
      }

      setMeta(data?.sub_schools?.meta || null);
      setOrgStats(data?.stats?.org || null);
    } catch (err) {
      // Ignore stale responses
      if (requestId !== latestRequestId.current) return;

      if (!isLoadingMore) {
        setSubSchools(null);
        setOrgStats(null);
        setMeta(null);
      }

      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      if (requestId === latestRequestId.current) {
        if (isLoadingMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    }
  };

  const hasMore = meta ? meta.current_page < meta.last_page : false;

  return {
    subSchools,
    orgStats,
    meta,
    loading,
    loadingMore,
    hasMore,
    error,
    getSubSchoolDropDownList,
  };
};
